import { getCoreUtils } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kJsonObject } from "../../../Admin/kReusableCoreTS/kJsonObject";

// AppChatState - THE CONTRACT for one chat's settings as the service knows them (2026-10-13): what `GET /chat/settings`
// (and `/chat/archive`) answers, read ONCE for a "Chat settings" dialog. Habitat-free (no DOM, no transport): the Bridge
// imports the basis words and `Bridge/src/tests/ChatStateContract.test.ts` reads the REAL service answer through `Parse`.
//
// ⛔ OWNER RULING 2026-10-13: "No property should ever be displayed as un-known". EVERY property ALWAYS has a concrete
// value: what WhatsApp gave (MEASURED), else WhatsApp's own meaning of absence (DEFAULTED) - not archived, not muted,
// timer off, not starred. `Measured` tells them apart for a TOOLTIP, never for a label.
// ⭐ THE ADDED FIELDS (all optional - an older service sends none, an older page ignores them):
//   `archiveBasis` / `muteBasis`: "observed" (WhatsApp sent this chat's own record), "complete" (no record, and the whole
//       collection was proved applied - absence then IS the answer), "partial" (no record; a snapshot was read that could
//       not be proved whole) or "default" (nothing read yet, or not possible right now).
//   `disappearingBasis`: "observed" (the timer's own setting event or history record), "message" (read off the latest
//       message the contact sent - WhatsApp stamps every message of a disappearing chat with the timer) or "default".
//   `starBasis`: "observed" (WACommander's own saved favourites were read) or "default" (they are still loading).
//   `resolving`: true while the service is reading the state from WhatsApp - a LOADING state with a deadline (the
//       service stops saying it `ChatArchiveSync.ResolveMs` after the read began); `recheckSec` = when to ask again.
// MEASURED = "observed", "complete", "message". DEFAULTED = "partial", "default".
// An older service that still says `known: false` is read as the default here too.
// A second consumer changes only the words (`Words`, `Source`); the reader is the wire.

/** One setting as read: its value, where it comes from, and whether it can be changed here. */
export class AppChatProp {
    /** True = WhatsApp (for a star: WACommander's own record) gave this value; false = the default for absence. */
    public Measured: boolean = false;
    public Basis: string = "default";
    /** archive/star: boolean; mute: -1 (for ever), 0 (not muted) or the end time in ms; disappearing: seconds (0 = off). */
    public Value: boolean | number = false;
    /** A CHANGE the owner asked for is awaiting WhatsApp's confirmation. */
    public Pending: boolean = false;
    /** The control may be used. False = disabled, with `AppChatStateDoc.Why` / the per-setting reason as its tooltip. */
    public Can: boolean = false;
    public Why: string = "";
}

export class AppChatStateDoc {
    public Ok: boolean = false;
    public Online: boolean = false;
    public ServerNowMs: number = 0;
    public RecheckSec: number = 0;
    /** The service is reading from WhatsApp right now: draw DEFAULTED values as loading, ask again after `RecheckSec`. */
    public Resolving: boolean = false;
    public Why: string = "";
    public Archive: AppChatProp = new AppChatProp();
    public Mute: AppChatProp = new AppChatProp();
    public Disappearing: AppChatProp = new AppChatProp();
    public Star: AppChatProp = new AppChatProp();
    /** ⭐ CHAT AND CONTACT ACTIONS (2026-10-14, `ChatActions` on the service) - all optional on the wire. */
    public Pin: AppChatProp = new AppChatProp();
    /** Value true = read on the phone; false = unread (`Unread` > 0) or marked unread (-1). */
    public Read: AppChatProp = new AppChatProp();
    public Block: AppChatProp = new AppChatProp();
    /** Value true only in the answer that confirmed the clear / delete (`confirmedAction`). */
    public Clear: AppChatProp = new AppChatProp();
    public Delete: AppChatProp = new AppChatProp();
    public Unread: number = 0;
    public Locked: boolean = false;
    /** The contact's own details (About, business profile, default timer) - only fields with a value; null = none yet. */
    public Info: AppContactInfo | null = null;
    /** Why the contact's details are not shown yet (the service's sentence), "" when there is nothing to say. */
    public InfoWhy: string = "";
}

/** The contact's details as the service read them from WhatsApp. Every field "" / [] / 0 = not shown. */
export class AppContactInfo {
    public About: string = "";
    public Business: boolean = false;
    public Description: string = "";
    public Category: string = "";
    public Address: string = "";
    public Website: string = "";
    public Email: string = "";
    public Hours: string[] = [];
    public DefaultTimer: number = 0;
}

export class AppChatState {
    public static readonly Observed: string = "observed";
    public static readonly Complete: string = "complete";
    public static readonly Partial: string = "partial";
    public static readonly Message: string = "message";
    public static readonly DefaultBasis: string = "default";
    public static readonly MaxRecheckSec: number = 600;
    public static readonly BasisField: Record<string, string> = { archive: "archiveBasis", mute: "muteBasis",
        disappearing: "disappearingBasis", star: "starBasis" };
    public static readonly WhyField: Record<string, string> = { archive: "why", mute: "muteWhy", disappearing: "disappearingWhy", star: "starWhy",
        pin: "pinWhy", read: "readWhy", block: "blockWhy", clear: "clearWhy", delete: "deleteWhy" };
    /** The settings that are a yes/no from the phone or an action - their value on the wire is a boolean. */
    public static readonly Flags: string[] = ["archive", "star", "pin", "read", "block", "clear", "delete"];
    public static readonly RecheckField: string = "recheckSec";
    public static readonly ResolvingField: string = "resolving";
    public static readonly LoadingWords: string = "Checking with WhatsApp…";

    /** DOCUMENTED EXCEPTION: pure word test - the bases that are MEASURED rather than defaulted. */
    public static MeasuredBasis = (Basis: string): boolean => {
        return Basis === AppChatState.Observed || Basis === AppChatState.Complete || Basis === AppChatState.Message;
    };

    /** The whole answer for `ChatId`; `Ok` false = not an answer about this chat (a refusal's sentence is in `Why`). */
    public static Parse = (Status: number, Text: string, ChatId: string): AppChatStateDoc => {
        const Out: AppChatStateDoc = new AppChatStateDoc();
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            AppChatState.Defaults(Out);
            const Doc: Record<string, unknown> | null = kJsonObject.ObjectOfJson(Text);
            if (Doc === null) { return Out; }
            if (typeof Doc.why === "string") { Out.Why = Doc.why; }
            Out.RecheckSec = AppChatState.Recheck(Doc[AppChatState.RecheckField]);
            if (Status < 200 || Status >= 300 || Doc.ok !== true || Doc.chatId !== ChatId) { return Out; }
            Out.Ok = true; Out.Online = Doc.online === true;
            Out.Resolving = Doc[AppChatState.ResolvingField] === true && Out.RecheckSec > 0;
            if (typeof Doc.serverNowMs === "number" && Number.isSafeInteger(Doc.serverNowMs) && Doc.serverNowMs > 0) { Out.ServerNowMs = Doc.serverNowMs; }
            Out.Archive = AppChatState.Prop(Doc, "archive", Doc.known, Doc.archived, Doc.pending, Doc.canArchive);
            Out.Mute = AppChatState.Prop(Doc, "mute", Doc.muteKnown, AppChatState.MuteValue(Doc.mutedUntilMs, Doc.muteKnown), Doc.mutePending, Doc.canMute);
            Out.Disappearing = AppChatState.Prop(Doc, "disappearing", Doc.disappearingKnown, Doc.disappearingSeconds, Doc.disappearingPending, Doc.canDisappearing);
            Out.Star = AppChatState.Prop(Doc, "star", Doc.starredKnown, Doc.starred, Doc.starPending, Doc.canStar);
            AppChatState.Actions(Out, Doc);
            return Out;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppChatState.Parse"); return new AppChatStateDoc(); }
    };

    /** The chat-action fields: present only on a service that has them (an older one sends none - the rows then stay disabled). */
    private static Actions = (Out: AppChatStateDoc, Doc: Record<string, unknown>): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (typeof Doc.unread === "number" && Number.isSafeInteger(Doc.unread) && Doc.unread >= -1) { Out.Unread = Doc.unread; }
            Out.Locked = Doc.locked === true;
            Out.Pin = AppChatState.Prop(Doc, "pin", typeof Doc.pinned === "boolean", Doc.pinned, Doc.pinPending, Doc.canPin);
            Out.Read = AppChatState.Prop(Doc, "read", typeof Doc.unread === "number", Out.Unread === 0, Doc.readPending, Doc.canRead);
            Out.Block = AppChatState.Prop(Doc, "block", typeof Doc.blocked === "boolean", Doc.blocked, Doc.blockPending, Doc.canBlock);
            Out.Clear = AppChatState.Prop(Doc, "clear", typeof Doc.canClear === "boolean", Doc.confirmedAction === "clear", false, Doc.canClear);
            Out.Delete = AppChatState.Prop(Doc, "delete", typeof Doc.canDelete === "boolean", Doc.confirmedAction === "delete", false, Doc.canDelete);
            if (typeof Doc.infoWhy === "string") { Out.InfoWhy = Doc.infoWhy; }
            Out.Info = AppChatState.InfoOf(Doc.info);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppChatState.Actions"); }
    };

    /** DOCUMENTED EXCEPTION: strict reader - each field its own type, bounded; anything else is left out. */
    public static InfoOf = (Raw: unknown): AppContactInfo | null => {
        if (Raw === null || typeof Raw !== "object" || Array.isArray(Raw)) { return null; }
        const R: Record<string, unknown> = Raw as Record<string, unknown>;
        const Out: AppContactInfo = new AppContactInfo();
        Out.About = AppChatState.TextOf(R.about, 700); Out.Business = R.business === true;
        Out.Description = AppChatState.TextOf(R.description, 1000); Out.Category = AppChatState.TextOf(R.category, 100);
        Out.Address = AppChatState.TextOf(R.address, 300); Out.Email = AppChatState.TextOf(R.email, 200);
        Out.Website = AppChatState.TextOf(R.website, 300); if (/^https?:\/\//i.test(Out.Website) === false) { Out.Website = ""; }
        if (Array.isArray(R.hours)) {
            for (const H of (R.hours as unknown[]).slice(0, 14)) { const One: string = AppChatState.TextOf(H, 60); if (One.length > 0) { Out.Hours.push(One); } }
        }
        if (typeof R.defaultTimer === "number" && Number.isSafeInteger(R.defaultTimer) && R.defaultTimer > 0 && R.defaultTimer <= 31536000) { Out.DefaultTimer = R.defaultTimer; }
        return Out;
    };
    /** DOCUMENTED EXCEPTION: pure - a string cut to `Max` code points, else "". */
    private static TextOf = (Value: unknown, Max: number): string => {
        if (typeof Value !== "string") { return ""; }
        return Array.from(Value.trim()).slice(0, Max).join("");
    };

    /** DOCUMENTED EXCEPTION: pure - a document that is not an answer still carries each setting's default value. */
    private static Defaults = (Out: AppChatStateDoc): void => {
        Out.Archive.Value = false; Out.Star.Value = false; Out.Mute.Value = 0; Out.Disappearing.Value = 0;
        Out.Pin.Value = false; Out.Read.Value = true; Out.Block.Value = false; Out.Clear.Value = false; Out.Delete.Value = false;
    };

    /** DOCUMENTED EXCEPTION: pure - the wire says `null` for "not muted"; the dialog reads 0. */
    private static MuteValue = (Until: unknown, Known: unknown): unknown => {
        if (Known === true && Until === null) { return 0; }
        return Until;
    };

    private static Prop = (Doc: Record<string, unknown>, Setting: string, Known: unknown, Value: unknown, Pending: unknown, Can: unknown): AppChatProp => {
        const Out: AppChatProp = new AppChatProp();
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            Out.Pending = Pending === true; Out.Can = Can === true;
            Out.Value = AppChatState.Default(Setting);
            const Why: unknown = Doc[AppChatState.WhyField[Setting]];
            if (typeof Why === "string") { Out.Why = Why; }
            if (Known !== true || AppChatState.ValueOk(Setting, Value) === false) { return Out; }
            let Basis: unknown = Doc[AppChatState.BasisField[Setting]];
            // An older service names no basis: what it calls known, it observed.
            if (Basis !== AppChatState.Partial && Basis !== AppChatState.DefaultBasis
                && (typeof Basis !== "string" || AppChatState.MeasuredBasis(Basis) === false)) { Basis = AppChatState.Observed; }
            Out.Basis = Basis as string; Out.Measured = AppChatState.MeasuredBasis(Out.Basis);
            Out.Value = Value as boolean | number;
            return Out;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppChatState.Prop"); return new AppChatProp(); }
    };

    /** DOCUMENTED EXCEPTION: pure constant - what WhatsApp means by "no record" for a setting. */
    public static Default = (Setting: string): boolean | number => {
        if (Setting === "read") { return true; }
        if (AppChatState.Flags.indexOf(Setting) >= 0) { return false; }
        return 0;
    };

    /** DOCUMENTED EXCEPTION: pure type test of one wire value. */
    private static ValueOk = (Setting: string, Value: unknown): boolean => {
        if (AppChatState.Flags.indexOf(Setting) >= 0) { return typeof Value === "boolean"; }
        if (typeof Value !== "number" || Number.isSafeInteger(Value) === false) { return false; }
        if (Setting === "mute") { return Value >= -1; }
        return Value >= 0 && Value <= 31536000;
    };

    /** DOCUMENTED EXCEPTION: pure - a whole number of seconds from 1 to `MaxRecheckSec`, else 0. */
    public static Recheck = (Value: unknown): number => {
        if (typeof Value !== "number" || Number.isSafeInteger(Value) === false || Value < 1) { return 0; }
        return Math.min(Value, AppChatState.MaxRecheckSec);
    };

    /**
     * The words for one setting's value. `NowMs` = the service's clock (`ServerNowMs`), for a mute's end.
     * ⛔ ALWAYS a value. The one other answer is the LOADING word, for a defaulted value while `Resolving` is true.
     */
    public static Words = (Setting: string, Prop: AppChatProp, NowMs: number, Resolving: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Prop.Measured === false && Resolving === true && Setting !== "star") { return AppChatState.LoadingWords; }
            return AppChatState.ValueWords(Setting, Prop.Value, NowMs);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppChatState.Words"); return ""; }
    };

    /** Where a value comes from - for a TOOLTIP only, never a label. */
    public static Source = (Setting: string, Prop: AppChatProp): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Setting === "star") { return "Saved in WACommander."; }
            if (Prop.Basis === AppChatState.Message) { return "Read from the latest message this contact sent."; }
            if (Prop.Measured === true) { return "As WhatsApp reports it."; }
            if (Setting === "disappearing") { return "WhatsApp has reported no timer for this chat."; }
            if (Setting === "block") { return "WhatsApp's blocklist has not been read on this connection yet."; }
            return "WhatsApp has reported no change for this chat.";
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppChatState.Source"); return ""; }
    };

    private static ValueWords = (Setting: string, Value: boolean | number, NowMs: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Setting === "archive") { if (Value === true) { return "Archived"; } return "Not archived"; }
            if (Setting === "star") { if (Value === true) { return "Starred"; } return "Not starred"; }
            if (Setting === "pin") { if (Value === true) { return "Pinned"; } return "Not pinned"; }
            if (Setting === "read") { if (Value === true) { return "Read"; } return "Unread"; }
            if (Setting === "block") { if (Value === true) { return "Blocked"; } return "Not blocked"; }
            if (Setting === "clear") { if (Value === true) { return "Cleared on your phone"; } return "Messages kept on your phone"; }
            if (Setting === "delete") { if (Value === true) { return "Deleted from your phone"; } return "In your phone's chat list"; }
            if (Setting === "mute") {
                if (Value === -1) { return "Muted"; }
                if (typeof Value === "number" && Value > 0 && (NowMs <= 0 || Value > NowMs)) { return "Muted for now"; }
                return "Not muted";
            }
            if (Value === 0 || Value === false) { return "Off"; }
            if (Value === 86400) { return "24 hours"; }
            if (Value === 604800) { return "7 days"; }
            if (Value === 7776000) { return "90 days"; }
            if (typeof Value === "number" && Value % 86400 === 0) { return String(Value / 86400) + " days"; }
            if (typeof Value === "number" && Value % 3600 === 0) { return String(Value / 3600) + " hours"; }
            return "On";
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppChatState.ValueWords"); return ""; }
    };
}
